import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { getActiveSedeId } from '../../../utils/partnerAccess/partnerStorage';
import { peekPartnerDirectory } from '../../../utils/partnerAccess/partnerDirectory';
import { Badge, BadgeLabel, BadgeMonogram } from './SedeBadge.style';

const MONOGRAM_LENGTH = 2;
const UNNAMED_SEDE = 'Sede';

// The color of a sede comes from the backend: only a plain hex color reaches
// the page, anything else keeps the neutral color of the stylesheet.
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const monogramStyle = (color) => (HEX_COLOR_PATTERN.test(color) ? { backgroundColor: color } : undefined);

const text = (value) => (typeof value === 'string' ? value.trim() : '');

const initialsOf = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MONOGRAM_LENGTH)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();

// The directory cached in the tab knows label, monogram and color. A tab
// opened straight into a sede has no cache yet: the name of the company the
// session was projected on stands in, with a neutral color. Nothing is
// requested for this: asking for the directory costs a partner session.
const describeSede = ({ sedeId, userId, companyName }) => {
  const directory = peekPartnerDirectory({ userId });
  const cached = directory ? directory.sedes.find((sede) => sede?.companyId === sedeId) : undefined;
  const label = text(cached?.label) || text(companyName);

  return {
    label: label || UNNAMED_SEDE,
    monogram: text(cached?.monogram) || initialsOf(label || UNNAMED_SEDE),
    color: text(cached?.color),
    title: label ? `Estás trabajando en la sede ${label}` : 'Estás trabajando en una sede',
    // What a screen reader calls the button: what it opens, then the sede.
    name: label ? `Sedes: ${label}` : 'Sedes',
  };
};

/**
 * Tells which sede this tab is working and, like the profile button next to
 * it, opens the profile menu, where the sedes are. Renders nothing outside
 * sede mode.
 */
const SedeBadge = ({ expanded, onClick }) => {
  const userId = useSelector((state) => state.user?.data?.id);
  const companyName = useSelector((state) => state.user?.data?.company?.name);
  const sedeId = getActiveSedeId();

  if (sedeId === null) return null;

  const { label, monogram, color, title, name } = describeSede({ sedeId, userId, companyName });

  return (
    <Badge
      type="button"
      title={title}
      aria-label={name}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      onClick={onClick}
    >
      <BadgeMonogram style={monogramStyle(color)} aria-hidden="true">
        {monogram}
      </BadgeMonogram>
      <BadgeLabel>{label}</BadgeLabel>
    </Badge>
  );
};

SedeBadge.propTypes = {
  // Whether the profile menu is open.
  expanded: PropTypes.bool.isRequired,
  // Opens the profile menu, or closes it when it is open.
  onClick: PropTypes.func.isRequired,
};

export default SedeBadge;
